import { createTourTranslationsConfig, type TourTranslation } from '@/config/objects/tour-translations';
import { CrudFormView, PageContainer, useApiClient } from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Tour translation edit form page
 */
export function TourTranslationFormPage() {
  const { id: tourId, translationId } = useParams<{ id: string; translationId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();

  const translationsConfig = useMemo(
    () => createTourTranslationsConfig(tourId || ''),
    [tourId],
  );

  // Fetch translation data for breadcrumbs
  const { data: translationResponse } = useQuery({
    queryKey: ['Translation', 'detail', translationId],
    queryFn: async () => {
      return apiClient.get<{ success: boolean; data: TourTranslation }>(
        `/tours/${tourId}/translations/${translationId}`
      );
    },
    enabled: !!tourId && !!translationId,
  });

  const translation = translationResponse?.data;

  if (!tourId || !translationId) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Translation Not Found</h2>
          <p className="text-muted-foreground">The requested translation could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Tours', href: '/tours' },
    { label: translation?.tour?.name || 'Tour', href: `/tours/${tourId}` },
    { label: 'Translations', href: `/tours/${tourId}#translations` },
    { label: translation?.language?.name || 'Translation', href: `/tours/${tourId}/translations/${translationId}` },
    { label: 'Edit', current: true },
  ];

  return (
    <PageContainer>
      <CrudFormView
        config={translationsConfig}
        itemId={translationId}
        breadcrumbs={breadcrumbs}
        onSuccess={() => navigate(`/tours/${tourId}/translations/${translationId}`)}
        onBack={() => navigate(`/tours/${tourId}/translations/${translationId}`)}
      />
    </PageContainer>
  );
}
